import {
  type GetRolesResponse,
  type RolesPerTeamSpace,
  type UpdateContributorRole,
  type UpdateRolesBody,
  type UpdateRolesPerLocale,
  type UpdateTeamSpaceRoles,
} from "@prismicio/editor-fields"

import { mainSpaceMeta } from "../../../app/services/role"

interface GetTeamSpaceRolesArgs {
  roles: GetRolesResponse
  teamSpaceId: string | undefined
}

export type TeamSpaceRoles = Partial<RolesPerTeamSpace[string]>

export function getTeamSpaceRoles(args: GetTeamSpaceRolesArgs): TeamSpaceRoles {
  const { roles, teamSpaceId } = args

  if (teamSpaceId === undefined) {
    return {}
  }

  if (teamSpaceId === mainSpaceMeta.id) {
    return roles.teamSpace ?? {}
  }

  return roles.teamSpace?.rolesPerTeamSpace?.[teamSpaceId] ?? {}
}

interface PrepareUpdateRolesBodyArgs {
  roleUpdates: UpdateTeamSpaceRoles
  teamSpaceId: string
}

export function prepareUpdateRolesBody(
  args: PrepareUpdateRolesBodyArgs,
): UpdateRolesBody {
  const { roleUpdates, teamSpaceId } = args

  if (teamSpaceId === mainSpaceMeta.id) {
    return {
      teamSpace: roleUpdates,
    }
  }

  return {
    teamSpace: {
      rolesPerTeamSpace: {
        [teamSpaceId]: roleUpdates,
      },
    },
  }
}

interface GetEffectiveRoleArgs {
  roles: TeamSpaceRoles
  roleUpdates: UpdateTeamSpaceRoles
  userId: string
  localeId: string
}

/**
 * Get the effective role for a user.
 * First get it from the update object, if not found, get it from the roles object.
 * If not found in the roles object, return "Readonly".
 */
export function getEffectiveRole(args: GetEffectiveRoleArgs) {
  const { roles, roleUpdates, userId, localeId } = args

  const rolesPerLocale = getRolesPerLocale({
    roles,
    roleUpdates,
    userId,
  })

  if (rolesPerLocale.roleUpdates === false) {
    return rolesPerLocale.roles?.[localeId] ?? "Readonly"
  }

  return (
    rolesPerLocale.roleUpdates?.[localeId] ??
    rolesPerLocale.roles?.[localeId] ??
    "Readonly"
  )
}

interface GetRolesPerLocaleArgs {
  roles: TeamSpaceRoles
  roleUpdates: UpdateTeamSpaceRoles
  userId: string
}

/**
 * Check if a user has roles for a specific locale.
 * First check the update object, if not found, check the roles object.
 * If not found in the roles object, return false.
 */
export function hasRolesPerLocale(args: GetRolesPerLocaleArgs): boolean {
  const { roles, roleUpdates, userId } = args

  const rolesPerLocale = getRolesPerLocale({
    roles,
    roleUpdates,
    userId,
  })

  if (rolesPerLocale.roleUpdates === false) {
    return false
  }

  return Boolean(rolesPerLocale.roleUpdates) || Boolean(rolesPerLocale.roles)
}

interface GetRolesPerLocaleArgs {
  roles: TeamSpaceRoles
  roleUpdates: UpdateTeamSpaceRoles
  userId: string
}

function getRolesPerLocale(args: GetRolesPerLocaleArgs) {
  const { roles, roleUpdates, userId } = args

  const isSpecificUser = userId !== "everyone"

  if (isSpecificUser) {
    return {
      roleUpdates: roleUpdates?.rolesPerUser?.[userId]?.rolesPerLocale,
      roles: roles?.rolesPerUser?.[userId]?.rolesPerLocale,
    }
  }

  return {
    roleUpdates: roleUpdates?.rolesPerLocale,
    roles: roles?.rolesPerLocale,
  }
}

type UpdateRolesPerLocaleArgs = {
  roleUpdates: UpdateTeamSpaceRoles
  userId: string
  update: RolePerLocalUpdate
}

export function updateRolesPerLocale(
  args: UpdateRolesPerLocaleArgs,
): UpdateTeamSpaceRoles {
  const { roleUpdates, userId, update } = args

  const isSpecificUser = userId !== "everyone"

  if (isSpecificUser) {
    return {
      ...roleUpdates,
      rolesPerUser: {
        ...roleUpdates?.rolesPerUser,
        [userId]: {
          ...roleUpdates?.rolesPerUser?.[userId],
          rolesPerLocale: updateRolesPerLocaleObject({
            rolesPerLocale: roleUpdates?.rolesPerUser?.[userId]?.rolesPerLocale,
            update,
          }),
        },
      },
    }
  }

  return {
    ...roleUpdates,
    rolesPerLocale: updateRolesPerLocaleObject({
      rolesPerLocale: roleUpdates?.rolesPerLocale,
      update,
    }),
  }
}

type RolePerLocalUpdate =
  | {
      action: "update"
      localeId: string
      role: UpdateContributorRole
    }
  | { action: "create"; localeId?: never; role?: never }
  | { action: "delete"; localeId?: never; role?: never }

type UpdateRolesPerLocaleObjectArgs = {
  rolesPerLocale?: UpdateRolesPerLocale
  update: RolePerLocalUpdate
}

function updateRolesPerLocaleObject(args: UpdateRolesPerLocaleObjectArgs) {
  const { rolesPerLocale, update } = args

  switch (update.action) {
    case "update":
      return {
        ...rolesPerLocale,
        [update.localeId]: update.role,
      }
    case "create":
      return {}
    case "delete":
      return false
  }
}
